import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import { IntlProvider } from 'react-intl';
import App from './App';
import { UserDetailsProvider } from './components/UserDetails/context';

const RTL_LANGUAGES = ['ar', 'dv', 'fa', 'he'];

function isValidLocale(locale) {
  try {
    const intl = new Intl.Locale(locale);
    return !!intl;
  } catch (error) {
    return false;
  }
}

function normalizeLocale(lang) {
  const lParts = lang.split('-');

  // 'pt' returns 'pt'
  // 'pt-br' and 'pt-BR' return 'pt_BR'
  // 'pt_BR' returns 'pt_BR'
  return lParts.length > 1 ? `${lParts[0]}_${lParts[1].toUpperCase()}` : lang;
}

function getLanguage() {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  if (typeof params.lang !== 'undefined') {
    return params.lang;
  }

  return 'fa_IR';
}

class Dashboard extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      intlMessages: {},
      intlLocale: getLanguage(),
    };

    this.setMessages();
    this.setRtl();
  }

  setMessages() {
    const fetchMessages = (lang) => new Promise((resolve, reject) => {
      const url = `/html5client/locales/${normalizeLocale(lang)}.json`;
      // 'no-cache' = always revalidate against the server (cheap 304 via etag) so updated
      // translations take effect on next load instead of being stuck on a stale cached copy.
      fetch(url, { cache: 'no-cache' }).then((response) => {
        if (!response.ok) return reject();
        return resolve(response.json());
      });
    });

    Promise.all([fetchMessages('en'), fetchMessages(getLanguage())])
      .then((values) => {
        let mergedMessages = {};

        if (values[0]) {
          mergedMessages = Object.assign(mergedMessages, values[0]);
        }

        if (values[1]) {
          mergedMessages = Object.assign(mergedMessages, values[1]);
        }

        this.setState({ intlMessages: mergedMessages });
      }).catch(() => {});
  }

  setRtl() {
    const { intlLocale } = this.state;
    const language = intlLocale.substring(0, 2);

    if (RTL_LANGUAGES.includes(language)) {
      document.body.parentNode.setAttribute('dir', 'rtl');
    } else {
      document.body.parentNode.setAttribute('dir', 'ltr');
    }

    // Keep <html lang> in sync with the locale (the static baseline in public/index.html is
    // fa; this updates it per actual locale, e.g. ?lang=en -> lang=en).
    document.body.parentNode.setAttribute('lang', language);
    document.body.classList.add(`lang-${language}`);
  }

  render() {
    const { intlLocale, intlMessages } = this.state;

    // Intl needs BCP-47 tags with hyphens: the underscore form 'fa_IR' is invalid and would make
    // IntlProvider fall back to 'en' (Gregorian dates / Latin numbers). Normalizing to 'fa-IR'
    // lets Intl use the Persian (Shamsi) calendar and Persian number formatting automatically.
    const bcp47Locale = intlLocale.replace(/_/g, '-');
    const locale = isValidLocale(bcp47Locale) ? bcp47Locale : undefined;

    return (
      <UserDetailsProvider>
        <IntlProvider defaultLocale="en" locale={locale} messages={intlMessages}>
          <App />
        </IntlProvider>
      </UserDetailsProvider>
    );
  }
}

const rootElement = document.getElementById('root');
ReactDOM.render(<Dashboard />, rootElement);
